/**
 * Represents the configuration of the card display feature for the PS Table component.
 *
 * A PSCard defines a card mode that can be applied to table rows, typically
 * for displaying additional information or actions in a card-like format. The component should expect a row property in the params object.
 *
 * @class PSCard
 * @example
 * // Create a card with string name
 * const card = new PSCard('card-component');
 *
 * // Create a card with full options
 * const card = new PSCard({
 *   name: 'card-component',
 *   enabled: true,
 *   is_active: true,
 *   params: { showActions: true }
 * });
 */
export default class PSCard {
	/**
	 * Creates a new PSCard instance.
	 *
	 * @param {string|Object} options - Card configuration options
	 * @param {string} [options.name] - The name/identifier of the card component
	 * @param {boolean} [options.enabled=true] - Whether card mode is allowed for this table
	 * @param {boolean} [options.is_active=false] - Whether card mode is currently active
	 * @param {Object} [options.params={}] - Additional parameters for the card component
	 */
	constructor(options) {
		// Handle string input by converting to object with name property
		if (typeof options === 'string')
			options = { name: options };

		options = options ?? {};

		this.enabled = ko_helper.safe_observable(options.enabled, true);

		this.is_active = ko_helper.safe_observable(options.is_active, false);
		this.name = ko_helper.safe_observable(options.name, null);
		this.params = ko_helper.safe_observable(options.params, {});

		/**
		 * Computed observable that determines if the table component should display cards or rows.
		 * Returns true if the card is enabled and the component is registered.
		 * @type {ko.PureComputed<boolean>}
		 */
		this.card_mode_supported = ko.pureComputed(() => {
			return this.enabled() && ko.components.isRegistered(this.name());
		});
	}
}
